import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { CHANGE_BOOKMAKER, CHANGE_BOOKMAKER_EXTEND } from "@/lib/redux/reducers/actionTypes";
import { BANKER, BANKER_EXTENDS } from "@/contants";
import BookmakerSelect from "@/components/BookmakerSelect";

import ICON_HOT_ICON from "@/assets/images/hot-icon.svg";
import starIcon from "@/assets/images/star.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const THU_VIET = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];

const buildDateOptions = (today, direction = 'forward') => {
  const options = [
    { value: "live", label: "Trực Tiếp", icon: ICON_HOT_ICON },
    { value: today.format("YYYY-MM-DD"), label: "Hôm nay" },
  ];

  if (direction === 'backward') {
    options.shift();
  }

  for (let i = 1; i <= 7; i++) {
    const d = direction === 'backward'
      ? today.clone().subtract(i, "day")
      : today.clone().add(i, "day");
    const thu = THU_VIET[d.day()];
    options.push({
      value: d.format("YYYY-MM-DD"),
      label: `${thu} (${d.format("DD/MM")})`,
    });
  }
  return options;
};

const FAVORITE_VALUE = "favorite";

const KbvFinanceeDateRange = ({ value, onChange, className = "", direction = 'forward', showBookmakerSelect = false }) => {
  const today = dayjs().tz(TZ);
  const options = useMemo(() => buildDateOptions(today, direction), [today, direction]);
  
  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    []
  );

  const bookmakerExtendOptions = useMemo(
    () => BANKER_EXTENDS.map((item) => ({ value: item.value, label: `Tỷ lệ ${item.shortName}` })),
    []
  );

  return (
    <div className={`kbv-date-range flex flex-col md:flex-row md:items-center md:justify-between gap-2.5 ${className}`}>
      <div className="flex items-center overflow-x-auto max-md:scrollbar-hide gap-2.5 w-full">
      <button
        type="button"
        onClick={() => onChange(FAVORITE_VALUE)}
        className={`kbv-date-btn kbv-date-favorite ${value === FAVORITE_VALUE ? "kbv-date-selected" : ""} hover:brightness-110 inline-flex items-center justify-center gap-1 px-2 py-1.5 min-h-[34px] rounded text-sm font-medium transition-colors cursor-pointer rounded-full whitespace-nowrap shrink-0
             ${
               value === FAVORITE_VALUE
                 ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-semibold"
                 : "bg-[var(--theme-bg)] text-[var(--theme-text)] font-medium"
             }`}
        aria-label="Trận yêu thích"
      >
        <img src={starIcon} alt="" className="kbv-date-icon w-4 h-4 object-contain" />
      </button>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`kbv-date-btn ${"live" == opt.value ? "kbv-date-live" : ""} ${value === opt.value ? "kbv-date-selected" : ""} ${today.format("YYYY-MM-DD") == opt.value ? "kbv-date-today" : ""} hover:brightness-110 inline-flex items-center gap-1 px-2 py-1.5 rounded text-sm items-center justify-center font-medium transition-colors cursor-pointer rounded-full whitespace-nowrap shrink-0
             ${"live" == opt.value ? "min-w-[100px]" : ""}
             
             ${today.format("YYYY-MM-DD") == opt.value ? "min-w-[80px]" : ""}

             ${
               value === opt.value
                 ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] font-semibold"
                 : "bg-[var(--theme-bg)] text-[var(--theme-text)] font-medium"
             }`}
        >
          {opt.icon && (
            <>
              <div
                className="kbv-date-live-ripple intro-banner-vdo-play-btn odds-absolute odds-t-0 odds-b-0 odds-text-center odds-m-auto pinkBg"
                aria-hidden
              >
                <span className="ripple odds-absolute pinkBg" />
                <span className="ripple odds-absolute pinkBg" />
                <span className="ripple odds-absolute pinkBg" />
              </div>
              <img
                src={opt.icon}
                alt=""
                className="kbv-date-icon w-4 h-4 object-contain"
              />
            </>
          )}

          <span className="kbv-date-label text-sm font-medium">
            {opt.label}
          </span>
        </button>
      ))}
      </div>
      {showBookmakerSelect && (
        <div className="flex items-center gap-2 w-full md:w-auto shrink-0">
          <BookmakerSelect
            value={bookmarkerExtend}
            onChange={(val) => dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })}
            options={bookmakerExtendOptions}
            placeholder="Tỷ lệ HONGKONG"
            className="w-1/2 md:w-[160px]"
            triggerClassName="px-[5px] py-[9px] text-sm"
            variant="outline"
          />
          <BookmakerSelect
            value={bookmarker}
            onChange={(val) => dispatch({ type: CHANGE_BOOKMAKER, payload: val })}
            options={bookmakerOptions}
            placeholder="Tất cả"
            className="w-1/2 md:w-[100px]"
            triggerClassName="px-[10px] py-[9px] text-sm"
          />
        </div>
      )}
    </div>
  );
};

export default KbvFinanceeDateRange;
export { FAVORITE_VALUE };
